import React, { useState, useEffect, useCallback } from 'react'
import DOMPurify from 'dompurify'
import {
  SimpleShowLayout,
  TextField,
  useRecordContext,
  useRefresh,
  useNotify,
} from 'react-admin'
import {
  Chip,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import PauseIcon from '@mui/icons-material/Pause'
import CheckIcon from '@mui/icons-material/Check'
import SendIcon from '@mui/icons-material/Send'
import EmailIcon from '@mui/icons-material/Email'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'

import Show from '../common/views/Show'
import DateField from '../common/fields/DateField'
import CampaignLeads from './CampaignLeads'
import { API_URL } from '../../services/settings'
import { campaignStatusColors } from './constants'

const CampaignTitle = ({ record }) => {
  return <span>{record ? record.name : ''}</span>
}

const StatusField = () => {
  const record = useRecordContext()
  if (!record) return null
  return (
    <Chip
      label={record.status}
      color={campaignStatusColors[record.status] || 'default'}
      size="small"
    />
  )
}

const StatsCards = () => {
  const record = useRecordContext()
  const [stats, setStats] = useState(null)

  const fetchStats = useCallback(async () => {
    if (!record) return
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_URL}/campaigns/${record.id}/stats`, {
        headers: { 'Authorization': `Bearer ${token}` },
      })
      if (response.ok) {
        const data = await response.json()
        setStats(data.data)
      }
    } catch (e) {
      // Silently fail
    }
  }, [record])

  useEffect(() => {
    fetchStats()
  }, [fetchStats])

  if (!stats) return null

  const cards = [
    { label: 'Total Leads', value: stats.total_leads },
    { label: 'Pending', value: stats.pending },
    { label: 'Sent', value: stats.sent },
    { label: 'Opened', value: stats.opened },
    { label: 'Open Rate', value: `${stats.open_rate}%` },
    { label: 'Kernel', value: stats.by_send_method?.kernel || 0 },
    { label: 'Lemlist', value: stats.by_send_method?.lemlist || 0 },
  ]

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 2, mb: 3 }}>
      {cards.map((card) => (
        <Card key={card.label} variant="outlined">
          <CardContent sx={{ textAlign: 'center', py: 1, '&:last-child': { pb: 1 } }}>
            <Typography variant="h5" color="primary">{card.value}</Typography>
            <Typography variant="caption" color="text.secondary">{card.label}</Typography>
          </CardContent>
        </Card>
      ))}
    </Box>
  )
}

const CampaignActions = () => {
  const record = useRecordContext()
  const refresh = useRefresh()
  const notify = useNotify()
  const [loading, setLoading] = useState(false)

  if (!record) return null

  const doAction = async (action) => {
    setLoading(true)
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_URL}/campaigns/${record.id}/${action}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })
      const data = await response.json()
      if (response.ok) {
        notify(data.message || 'Action completed', { type: 'success' })
        refresh()
      } else {
        notify(data.message || 'Action failed', { type: 'error' })
      }
    } catch (e) {
      notify('Error: ' + e.message, { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
      {record.status === 'draft' && (
        <Button
          variant="contained"
          color="success"
          startIcon={<PlayArrowIcon />}
          onClick={() => doAction('activate')}
          disabled={loading}
          size="small"
        >
          Activate
        </Button>
      )}
      {record.status === 'active' && (
        <>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<PauseIcon />}
            onClick={() => doAction('pause')}
            disabled={loading}
            size="small"
          >
            Pause
          </Button>
          <Button
            variant="outlined"
            startIcon={<CheckIcon />}
            onClick={() => doAction('complete')}
            disabled={loading}
            size="small"
          >
            Complete
          </Button>
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={() => doAction('send')}
            disabled={loading}
            size="small"
          >
            Send Now
          </Button>
        </>
      )}
      {record.status === 'paused' && (
        <>
          <Button
            variant="contained"
            color="success"
            startIcon={<PlayArrowIcon />}
            onClick={() => doAction('activate')}
            disabled={loading}
            size="small"
          >
            Resume
          </Button>
          <Button
            variant="outlined"
            startIcon={<CheckIcon />}
            onClick={() => doAction('complete')}
            disabled={loading}
            size="small"
          >
            Complete
          </Button>
        </>
      )}
      <Button
        variant="outlined"
        startIcon={<EmailIcon />}
        onClick={() => doAction('send_test')}
        disabled={loading}
        size="small"
      >
        Send Test
      </Button>
    </Box>
  )
}

const ImproveMessageButton = () => {
  const record = useRecordContext()
  const notify = useNotify()
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState(null)

  if (!record) return null

  const handleImprove = async (field) => {
    setLoading(true)
    setResult(null)
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_URL}/campaigns/${record.id}/improve_message`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ field }),
      })
      const data = await response.json()
      if (response.ok) {
        setResult(data.data.improved)
      } else {
        notify(data.message || 'Failed to improve', { type: 'error' })
      }
    } catch (e) {
      notify('Error: ' + e.message, { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const handleCopy = () => {
    if (result) {
      navigator.clipboard.writeText(result)
      notify('Copied to clipboard', { type: 'success' })
      setOpen(false)
    }
  }

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<AutoFixHighIcon />}
        onClick={() => setOpen(true)}
        size="small"
      >
        Improve Message
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Improve Message with AI</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Button variant="contained" onClick={() => handleImprove('message')} disabled={loading} size="small">
              Improve Main Message
            </Button>
            <Button variant="outlined" onClick={() => handleImprove('followup')} disabled={loading} size="small">
              Generate Follow-up
            </Button>
          </Box>
          {loading && <CircularProgress size={24} />}
          {result && (
            <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Result:</Typography>
              <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(result) }} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
          {result && <Button onClick={handleCopy} variant="contained">Copy to Clipboard</Button>}
        </DialogActions>
      </Dialog>
    </>
  )
}

const MessagePreview = () => {
  const record = useRecordContext()
  if (!record) return null

  return (
    <Box sx={{ mb: 3 }}>
      {record.subject && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary">First Message</Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>{record.subject}</Typography>
            {record.message && (
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
                <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(record.message) }} />
              </Box>
            )}
          </CardContent>
        </Card>
      )}
      {record.followup_subject && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary">
              Follow-up ({record.followup_delay_days} days later)
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>{record.followup_subject}</Typography>
            {record.followup_message && (
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
                <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(record.followup_message) }} />
              </Box>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  )
}

const CampaignShowContent = () => {
  return (
    <Box>
      <SimpleShowLayout>
        <TextField source="name" />
        <StatusField label="Status" />
        <TextField source="default_send_method" label="Send Method" />
        <TextField source="description" />
        <TextField source="objectives" />
        <DateField source="created_at" />
        <DateField source="updated_at" />
      </SimpleShowLayout>

      <Box sx={{ mx: 2, mb: 2 }}>
        <CampaignActions />
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <ImproveMessageButton />
        </Box>
      </Box>

      <Box sx={{ mx: 2 }}>
        <StatsCards />
      </Box>

      <Box sx={{ mx: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Messages</Typography>
        <MessagePreview />
      </Box>

      <Box sx={{ mx: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Leads</Typography>
        <CampaignLeads />
      </Box>
    </Box>
  )
}

const CampaignShow = (props) => (
  <Show title={<CampaignTitle />} {...props}>
    <CampaignShowContent />
  </Show>
)

export default CampaignShow
